import Member from '@@types/Member';
import MemberResponse from '@@types/Member/Response';

import Actions from '~/stores/Actions';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { membersStore } from '~/stores/MembersStore';
import { api } from '~/stores/providers/Api2';
import { userStore } from '~/stores/UserStore';
import { fixLocale, fixTimezone } from '~/utils/memberUtil';
import { promise } from 'fluture';
import { converge, curry, mergeRight, pipe, tap } from 'ramda';

class BootstrapUser {
  constructor(private actions: Actions) {}

  fetchUser = async () => {
    await globalSettingStore.cacheOrFetchEnv();
    const [auth, userResponse] = await Promise.all([
      userStore.fetchAndCache(),
      promise(this.actions.memberActions.fetchUser()),
    ]);
    userStore.fetchOrganization(auth.orgId);
    this.handleUserResponse(userResponse);
    return [auth, userResponse.content];
  };

  handleUserResponse = ({ content: user }: MemberResponse) => {
    userStore.setIdProviderId(user.idProviderId);
    userStore.setTenantMemberRole(user.tenantMemberRole);
    pipe(
      converge(mergeRight, [fixLocale, fixTimezone]),
      tap(this.updateLocaleAndTimezone(user)),
      this.setLocaleAndTimezone,
      this.actions.officeHoursActions.revalidateMeInWork
    )(user);
  };

  private updateLocaleAndTimezone = curry((user: Member, params) => {
    if (user.locale === null || user.timezoneName === null) {
      api.me.updateUser(params).then(() => {
        userStore.setUpdateingLocaleInfo(false);
      });
      return;
    }
    userStore.setUpdateingLocaleInfo(false);
  });

  private setLocaleAndTimezone = ({ locale, timezoneName }: any) => {
    this.setIntlLocale(locale);
    this.setTimezone(timezoneName);
  };

  private setIntlLocale = (locale: string) => {
    membersStore.updateMember({ id: userStore.id, locale });
    api.me.notifyDefaultLang(locale);
  };

  private setTimezone = (timezoneName: string) => {
    userStore.setTimezone(timezoneName);
  };

  private fetchTenantOrganizationCount = () => {
    userStore.fetchTenantOrganizationCount();
  };

  initialize = async (callback) => {
    const [[auth, user]] = await Promise.all([this.fetchUser(), this.actions.settingActions.fetchUserPreferences()]);
    await callback(auth, user);
    this.fetchTenantOrganizationCount();
  };
}

export default BootstrapUser;
